/**
 * 서버에 도달하지 못한 요청 판별.
 *
 * 요청이 서버에 닿으면 응답이 온다 — 4xx·5xx 라도 `response` 가 있다. 반대로 응답이
 * 없다는 건 요청이 서버까지 가지 못했다는 뜻이다(연결 거부·DNS 실패·확장/DevTools 차단).
 * 그 구분이 필요한 곳은 **요청 한도(throttle)를 클라이언트가 흉내낼 때**다: 한도를 세는
 * NestJS throttler 가드는 요청이 도착해야 돌기 때문에, 도착하지 못한 요청은 한도를 한 칸도
 * 깎지 않는다. 그런 실패에 쿨다운을 걸면 사용자를 아무 이유 없이 붙잡는다.
 *
 * 판별을 응답 유무로 하는 선례는 이미 `queryClient.ts` 의 `shouldRetry` 에 있다
 * (`if (!error.response) return true; // Network error - retry`).
 *
 * 타임아웃은 이 판별로 가려지지 않는다 — 서버가 요청을 받아 처리하다 `apiClient` 의
 * timeout(10초, client.ts)을 넘기면 서버는 한도를 셌는데 여기서는 응답 없는 에러로 보인다.
 * 즉 이 함수가 true 인 경우가 "한도를 안 깎았다"를 보장하지는 않는다. 그 구멍을 감수하는
 * 대신 흔한 네트워크 끊김에서 사용자를 붙잡지 않기로 한 결정이다(2026-08-19).
 */

import { AxiosError } from 'axios';

export function isUnreachedRequestError(error: unknown): boolean {
  return error instanceof AxiosError && !error.response;
}

/**
 * 응답이 오기 전에 브라우저 쪽에서 끊긴 요청 — 시간 초과(`timeout`)와 브라우저가 죽인 XHR.
 *
 * 이 실패는 **서버가 일을 끝냈는지 모른다.** 서버는 끊긴 줄 모르고 끝까지 처리하므로, 쓰기
 * 요청이라면 결과를 서버 상태로 다시 확인해야 한다. 사용자 취소(`ERR_CANCELED`)는 여기 들지
 * 않는다 — 그건 `isRequestCanceledError` 몫이다.
 */
export function isRequestTimeoutError(error: unknown): boolean {
  return (
    error instanceof AxiosError &&
    !error.response &&
    (error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ETIMEDOUT)
  );
}
